import React, { createContext, useContext, useState, useEffect } from 'react';

interface User {
  name: string;
  email: string;
  user_type: string;
  publisher_id?: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  login: (token: string, userType: string, email: string, name: string, publisherId?: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const userType = localStorage.getItem('userType');
    const email = localStorage.getItem('email');
    const name = localStorage.getItem('name');
    const publisherId = localStorage.getItem('publisherId');

    if (storedToken && userType && email && name) {
      setIsAuthenticated(true);
      setToken(storedToken);
      setUser({
        name,
        email,
        user_type: userType,
        publisher_id: publisherId || undefined
      });
    }
  }, []);

  const login = (token: string, userType: string, email: string, name: string, publisherId?: string) => {
    localStorage.setItem('token', token);
    localStorage.setItem('userType', userType);
    localStorage.setItem('email', email);
    localStorage.setItem('name', name);
    if (publisherId) {
      localStorage.setItem('publisherId', publisherId);
    } else {
      localStorage.removeItem('publisherId');
    }
    setIsAuthenticated(true);
    setToken(token);
    setUser({
      name,
      email,
      user_type: userType,
      publisher_id: publisherId
    });
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userType');
    localStorage.removeItem('email');
    localStorage.removeItem('name');
    localStorage.removeItem('publisherId');
    setIsAuthenticated(false);
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, token, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 